import i18next from 'i18next'
import { defaultTo, isEmpty } from 'lodash'
import moment from 'moment'
import { apolloClient } from '~/common/apollo'
import { handleSubmitFiles } from '~/common/helpers'
import { action, computed, observable, store } from '~/common/mobx.decorator'
import { notifyStore } from '~/stores'
import {
	CREDENTIAL_LIST_QUERY,
	CREDENTIAL_UPDATE_MUTATION,
	GET_USER_FORM_QUERY,
	SIGN_JOTfORM_MUTATION,
	GET_WORKER_CREDENTIAL_QUERY,
} from './care-credential.graphql'
import { DOCUMENT_ACTIONS } from './credentials-feature.constant'
import { GET_SELECT_OPTIONS } from '~/common/apollo/queries/user.query'

@store()
class CareCredentialStore {
	@observable credentials = []
	@observable selectedCredential = {}
	@observable documentActionType = ''
	@observable userForm = {}
	@observable selectOptionsByDataType = {}
	@observable loadingOptions = false
	@observable esignUrl = ''
	@observable showSkillChecklist = false

	@computed
	get isImmigrationRoadMap() {
		if (!this.selectedCredential) return false
		return this.selectedCredential?.credential?.externalId === 'read_only_immigration_road_map_rn'
	}

	@computed
	get mappedCredentials() {
		return this.credentials.map((credential) => ({
			...credential,
			files: credential.files.map((file) => ({
				...file.file,
				updatedAt: moment(file?.updatedAt).format('DD MMMM, YYYY'),
				recordId: file.id,
				fileId: file.file.id,
			})),
			state: {
				answer: credential?.state,
			},
			issuingAuthority: {
				answer: credential?.issuingAuthority,
			},
			licenseType: {
				answer: credential?.licenseType,
			},
			number: {
				answer: credential?.number,
			},
			expirationDate: {
				answer: credential?.expirationDate,
			},
			effectiveDate: {
				answer: credential?.effectiveDate,
			},
		}))
	}

	@computed
	get wizardForm() {
		return this.userForm?.wizardForm?.quizzes?.[0]?.quizQuestions
	}

	@action
	setDocumentActionType = (type) => {
		this.documentActionType = type
	}

	@action
	setSelectedCredential = (credential) => {
		this.selectedCredential = credential
	}

	@action
	clearSelectedCredential = () => {
		this.selectedCredential = {}
	}

	@action
	fetchCredentialForm = async (externalId) => {
		try {
			const name = `credential_form_${externalId}`
			const {
				data: { userForm },
			} = await apolloClient.query({ query: GET_USER_FORM_QUERY, variables: { name } })

			this.userForm = userForm
			return
		} catch (error) {
			this.userForm = {}
			console.error('Error fetching credential form:', error)
		}
	}

	@action
	fetchCredentials = async () => {
		const {
			data: { workerCredentials },
		} = await apolloClient.query({ query: CREDENTIAL_LIST_QUERY })

		this.credentials = defaultTo(workerCredentials, [])
	}

	@action
	updateCredential = async (variables) => {
		try {
			const locations = variables?.locations?.answer || ''
			const locationArray = locations ? locations?.split(',').map((location) => location.trim()) : null

			const response = await apolloClient.mutate({
				mutation: CREDENTIAL_UPDATE_MUTATION,
				variables: {
					id: variables?.id,
					files: handleSubmitFiles(variables?.files, this.selectedCredential?.files),
					submissionDate: new Date().toISOString(),
					issuingAuthority: variables?.issuingAuthority?.answer || null,
					expirationDate: variables?.expirationDate?.answer || null,
					number: variables?.number?.answer || null,
					locations: locationArray?.length > 0 ? locationArray : null,
					effectiveDate: variables?.effectiveDate?.answer || null,
					licenseType: variables?.licenseType?.answer || null,
					notes: variables?.notes?.answer || null,
				},
			})
			if (response?.data?.updateWorkerCredential?.id) {
				this.fetchCredentials()
				notifyStore.success(i18next.t('CREDENTIAL_SUBMITT_SUCCESS'))
			}
		} catch (error) {
			const { graphQLErrors } = error
			notifyStore.error(graphQLErrors?.[0]?.message)
		}
	}

	@action
	declineCredential = async (credentialId) => {
		try {
			const response = await apolloClient.mutate({
				mutation: CREDENTIAL_UPDATE_MUTATION,
				variables: {
					id: credentialId,
					status: 'decline',
				},
			})
			if (response?.data?.updateWorkerCredential?.id) {
				this.fetchCredentials()
				notifyStore.success(i18next.t('DECLINE_CREDENTIAL_SUCCESS'))
			}
		} catch (error) {
			const { graphQLErrors } = error
			notifyStore.error(graphQLErrors?.[0]?.message)
		}
	}

	@action
	signJotForm = async (credentialId) => {
		try {
			const response = await apolloClient.mutate({
				mutation: SIGN_JOTfORM_MUTATION,
				variables: {
					id: credentialId,
				},
			})
			return response?.data?.markSignedWorkerCredential?.success
		} catch (error) {
			const { graphQLErrors } = error
			notifyStore.error(graphQLErrors?.[0]?.message)
		}
	}

	checkDocumentActionType = (credential) => {
		const { credential: credentialDetail } = credential

		if (credentialDetail?.changable === false) {
			return DOCUMENT_ACTIONS.no_action
		} else if (credential?.esignUrl) {
			return DOCUMENT_ACTIONS.webform
		} else if (credentialDetail?.fileSample?.id && isEmpty(credential?.files)) {
			return DOCUMENT_ACTIONS.download
		} else if (credentialDetail?.fileSample?.id) {
			return DOCUMENT_ACTIONS.upload_and_download
		}

		return DOCUMENT_ACTIONS.upload
	}

	@action
	fetchQuestionOptions = async (dataType, payload = {}, loadMore = false) => {
		try {
			this.loadingOptions = true

			const currentData = this.getOptionsViaDataType(dataType)

			const res = await apolloClient.query({
				query: GET_SELECT_OPTIONS,
				variables: {
					filter: {
						dataType,
						payload,
					},
				},
			})

			const { selectOptions } = res.data || {}
			if (!selectOptions) {
				console.error('No select options')
				return
			}

			const { nodes, pageInfo } = selectOptions || {}
			const remappedOptions = nodes.map(({ id, name }) => ({
				value: id,
				label: name,
			}))

			if (loadMore && currentData?.data) {
				this.setOptionsViaDataType(dataType, {
					data: [...currentData.data, ...remappedOptions],
					rest: { pageInfo },
				})
			} else {
				this.setOptionsViaDataType(dataType, {
					data: remappedOptions,
					rest: { pageInfo },
				})
			}

			return selectOptions
		} catch (e) {
			notifyStore.error(e?.message || "Couldn't fetch question options")
			console.error(e)
		} finally {
			this.loadingOptions = false
		}
	}

	@action
	setOptionsViaDataType = (dataType, options) => {
		this.selectOptionsByDataType[dataType] = options
	}

	@action
	getOptionsViaDataType = (dataType) => {
		return this.selectOptionsByDataType[dataType] || {}
	}

	/**
	 * @param {PROFILE_COMPLETION_FIELDS_NAME} dataType
	 */
	@action
	getOptionsDataViaDataType = (dataType) => {
		if (!this.selectOptionsByDataType) return []
		const { data } = this.selectOptionsByDataType[dataType] || []

		return data
	}

	@action
	fetchSingleCredential = async (credentialId) => {
		try {
			const {
				data: { workerCredential },
			} = await apolloClient.query({
				query: GET_WORKER_CREDENTIAL_QUERY,
				variables: { id: credentialId },
				fetchPolicy: 'network-only',
			})

			if (workerCredential) {
				// Update credential trong list hiện tại
				this.credentials = this.credentials.map((cred) => {
					if (cred.id === credentialId) {
						return {
							...cred,
							files: workerCredential.files,
							credential: {
								...cred.credential,
								fileSample: workerCredential.credential?.fileSample,
							},
						}
					}
					return cred
				})
				return workerCredential
			}
			return null
		} catch (error) {
			notifyStore.error(error?.message)
			return null
		}
	}

	@action
	setEsignUrl = (value) => {
		this.esignUrl = value
	}

	@action
	setShowSkillChecklist = (value) => {
		this.showSkillChecklist = value
	}
}

export const careCredentialStore = new CareCredentialStore()
